// Controls
import { Breadcrumb, BreadcrumbButton, BreadcrumbDivider, BreadcrumbItem, mergeClasses, Tag, Tooltip } from "@fluentui/react-components";
import { IconButton } from "@ms/planner-generic-controls/lib/local/iconButton/IconButton";
import { Divider, LinkDismiss, MyPlansBundled, PinBundled, PinFilled } from "@ms/planner-generic-controls/lib/local/icons";
import { VirtualContainer } from "@ms/planner-generic-controls/lib/local/virtualization/VirtualContainer";
import { ConnectedPlansButton } from "../../connectedPlansButton/ConnectedPlansButton";
import { PlanContextDropdown } from "../../dropdown/planContextDropdown/PlanContextDropdown";
import { PlanIcon } from "../../planIcon/PlanIcon";
import { ContainerType } from "@ms/planner-models/lib/local/service/planner/enumerations/IPlannerEnumerations";
// Resources
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
// Styles
import { usePlanHeaderInfoStyles } from "./PlanHeaderInfo.styles";
// Utilities
import * as React from "react";
/**
 * React function component to render plan header info section with breadcrumbs
 * @param props Plan header info props
 * @returns Returns plan header info section
 */ export const PlanHeaderInfo = ({ isCurrentPlanPinned, plan, imageSource, planBreadcrumbItem, myPlansBreadcrumbItem, togglePlanPinUnpinStatus, isTabView, onRenderPlanPhoto, renderContentSensitivityLabelContainer, isReadOnly, staticMenuItems, containerDisplayName, isConnectedPlansEnabled, connectedPlans = [], onClickConnectedTask, onOpenConnectedPlansMenu })=>{
    const classes = usePlanHeaderInfoStyles();
    /**
     * Renders the plan icon using PlanIcon and VirtualContainer like the original
     */ const renderPlanIcon = ()=>{
        const planIcon = /*#__PURE__*/ React.createElement("div", {
            className: classes.planAvatar,
            "aria-hidden": "true"
        }, /*#__PURE__*/ React.createElement(PlanIcon, {
            containerType: plan.container.containerType,
            containerPhoto: imageSource || undefined,
            name: plan.displayName,
            size: 24
        }));
        return /*#__PURE__*/ React.createElement(VirtualContainer, {
            containerId: plan.id,
            onLoad: onRenderPlanPhoto
        }, planIcon);
    };
    /**
     * Renders the pin/unpin button
     */ const renderPinUnpinButton = ()=>{
        const title = isCurrentPlanPinned ? Strings.PlanHeader_PlanHeaderRemovePinned : Strings.PlanHeader_PlanHeaderAddPinned;
        const pinIcon = isCurrentPlanPinned ? /*#__PURE__*/ React.createElement(PinFilled, null) : /*#__PURE__*/ React.createElement(PinBundled, null);
        return /*#__PURE__*/ React.createElement(IconButton, {
            appearance: "transparent",
            icon: pinIcon,
            onClick: togglePlanPinUnpinStatus,
            className: classes.pinButton,
            title: title,
            "data-testid": "pin-unpin-button"
        });
    };
    /**
     * Renders the delinked icon
     */ const delinkSection = ()=>{
        const visibleContextLinks = plan.contexts.filter((link)=>link.isVisible());
        if (visibleContextLinks.length > 0) {
            const delinkedContext = plan.contexts.find((link)=>{
                return link.isActive === false;
            });
            // If delinked context exists, render delinked icon
            if (delinkedContext != null) {
                return /*#__PURE__*/ React.createElement("div", {
                    className: classes.delinkIcon
                }, /*#__PURE__*/ React.createElement(Divider, {
                    className: classes.divider
                }), /*#__PURE__*/ React.createElement(Tooltip, {
                    relationship: "description",
                    content: Strings.PlanHeader_PlanContextDelinkedTooltip
                }, /*#__PURE__*/ React.createElement("span", {
                    tabIndex: 0,
                    role: "img"
                }, /*#__PURE__*/ React.createElement(LinkDismiss, {
                    className: classes.dismiss
                }))));
            }
        }
        return null;
    };
    const renderPlanContextDropdown = ()=>{
        const visibleContextLinks = plan.contexts.filter((link)=>link.isVisible());
        if (plan.container.containerType === ContainerType.ToDo) {
            return null;
        }
        if (visibleContextLinks.length > 0 || staticMenuItems) {
            return /*#__PURE__*/ React.createElement(PlanContextDropdown, {
                title: plan.displayName,
                visibleContextLinks: visibleContextLinks,
                containerDisplayName: containerDisplayName,
                delinkSection: delinkSection(),
                staticMenuItems: staticMenuItems
            });
        }
        return null;
    };
    /**
     * Renders the title side tag for read-only plans
     */ const renderTitleSideTag = ()=>{
        const tagLabel = Strings.PlanHeader_ReadOnlyTag;
        return /*#__PURE__*/ React.createElement(Tag, {
            tabIndex: 0,
            className: classes.titleSideTag
        }, /*#__PURE__*/ React.createElement("span", {
            className: classes.titleSideTagText
        }, tagLabel));
    };
    /**
     * Renders the connected plans button, shown when the plan has one or more parent-task PLinks
     */ const shouldRenderConnectedPlansButton = isConnectedPlansEnabled && connectedPlans.length > 0 && !!onClickConnectedTask;
    const renderConnectedPlansButton = ()=>{
        if (!shouldRenderConnectedPlansButton || !onClickConnectedTask) {
            return null;
        }
        return /*#__PURE__*/ React.createElement(ConnectedPlansButton, {
            connectedPlans: connectedPlans,
            onClickConnectedTask: onClickConnectedTask,
            onOpenConnectedPlansMenu: onOpenConnectedPlansMenu
        });
    };
    if (isTabView) {
        return /*#__PURE__*/ React.createElement("div", {
            className: classes.root,
            "data-testid": "plan-header-info"
        }, /*#__PURE__*/ React.createElement("div", {
            className: classes.planTitle
        }, /*#__PURE__*/ React.createElement(Tooltip, {
            content: planBreadcrumbItem.text,
            relationship: "description"
        }, /*#__PURE__*/ React.createElement("span", {
            className: classes.planTitleText,
            tabIndex: 0
        }, planBreadcrumbItem.text))), renderContentSensitivityLabelContainer(), renderConnectedPlansButton(), renderPlanContextDropdown(), isReadOnly && renderTitleSideTag());
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: classes.root,
        "data-testid": "plan-header-info"
    }, /*#__PURE__*/ React.createElement(Breadcrumb, {
        "data-testid": "plan-header-info-breadcrumb",
        size: "large",
        className: classes.breadcrumb,
        list: {
            className: classes.breadcrumbList
        }
    }, /*#__PURE__*/ React.createElement(BreadcrumbItem, {
        "data-testid": "plan-header-info-breadcrumb-item",
        key: myPlansBreadcrumbItem.key,
        className: classes.breadcrumbItem
    }, /*#__PURE__*/ React.createElement(BreadcrumbButton, {
        onClick: myPlansBreadcrumbItem.onClick,
        href: myPlansBreadcrumbItem.href,
        className: classes.breadcrumbButton,
        "data-testid": "plan-header-my-plans-breadcrumb-button",
        icon: {
            children: /*#__PURE__*/ React.createElement(MyPlansBundled, {
                "data-testid": "plan-header-my-plans-breadcrumb-icon"
            })
        }
    }, /*#__PURE__*/ React.createElement("span", {
        className: classes.myPlansBreadcrumbText
    }, myPlansBreadcrumbItem.text))), /*#__PURE__*/ React.createElement(BreadcrumbDivider, {
        key: "divider"
    }), shouldRenderConnectedPlansButton && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(BreadcrumbItem, {
        key: "connected-plans",
        className: classes.breadcrumbItem
    }, renderConnectedPlansButton()), /*#__PURE__*/ React.createElement(BreadcrumbDivider, {
        key: "connected-plans-divider"
    })), /*#__PURE__*/ React.createElement(BreadcrumbItem, {
        "data-testid": "plan-header-info-plan-breadcrumb-item",
        key: planBreadcrumbItem.key,
        className: mergeClasses(classes.breadcrumbItem, classes.planTitleContainer)
    }, /*#__PURE__*/ React.createElement(Tooltip, {
        content: planBreadcrumbItem.text,
        relationship: "label"
    }, /*#__PURE__*/ React.createElement(BreadcrumbButton, {
        "data-testid": "plan-header-plan-breadcrumb-button",
        onClick: planBreadcrumbItem.onClick,
        href: planBreadcrumbItem.href,
        icon: renderPlanIcon(),
        className: mergeClasses(classes.breadcrumbButton, classes.planTitle),
        current: planBreadcrumbItem.onClick === undefined
    }, /*#__PURE__*/ React.createElement("span", {
        className: classes.planTitleText
    }, planBreadcrumbItem.text))))), renderContentSensitivityLabelContainer(), renderPinUnpinButton(), renderPlanContextDropdown(), isReadOnly && renderTitleSideTag());
};
